/* Native discount links work without JavaScript; enhance only code copying. */
if (!customElements.get('lx-drop-offer')) {
  customElements.define('lx-drop-offer', class extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      const button = this.querySelector('[data-copy-code]');
      const code = this.querySelector('code');
      const status = this.querySelector('[role="status"]');
      if (!button || !code || !status || !navigator.clipboard) return;
      this.dataset.ready = 'true';
      button.hidden = false;
      button.addEventListener('click', async () => {
        this.dataset.copyFeedback = 'true';
        try {
          await navigator.clipboard.writeText(code.textContent.trim());
          button.textContent = 'Copied';
          status.textContent = 'Code copied. Use it at checkout.';
        } catch (_) {
          const selection = window.getSelection();
          const range = document.createRange();
          range.selectNodeContents(code);
          selection.removeAllRanges();
          selection.addRange(range);
          status.textContent = 'Select and copy the code above.';
        }
      });
    }
  });
}

// Optional desktop arrows enhance a native, keyboard-scrollable product row.
if (!customElements.get('lx-drop-product-rail')) {
 customElements.define('lx-drop-product-rail', class extends HTMLElement {
  connectedCallback() {
   this.track = this.querySelector('.lx-drop-items');
   this.previous = this.querySelector('[data-drop-prev]');
   this.next = this.querySelector('[data-drop-next]');
   if (!this.track || !this.previous || !this.next) return;
   this.abort = new AbortController();
   const signal = this.abort.signal;
   this.refresh = () => {
    this.previous.disabled = this.track.scrollLeft <= 2;
    this.next.disabled = this.track.scrollLeft >= this.track.scrollWidth - this.track.clientWidth - 2;
   };
   const move = direction => {
    const card = this.track.firstElementChild;
    const step = card.getBoundingClientRect().width + parseFloat(getComputedStyle(this.track).columnGap);
    this.track.scrollBy({left:direction * step, behavior:matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
   };
   this.previous.addEventListener('click', () => move(-1), {signal});
   this.next.addEventListener('click', () => move(1), {signal});
   this.track.addEventListener('scroll', this.refresh, {passive:true, signal});
   this.resize = new ResizeObserver(this.refresh);
   this.resize.observe(this.track);
   this.refresh();
   this.dataset.ready = 'true';
  }
  disconnectedCallback() { this.abort?.abort(); this.resize?.disconnect(); delete this.dataset.ready; }
 });
}

/* Editorial enhancement: keep original HTML usable without JavaScript. */
if (!customElements.get('lx-drop-story')) {
  customElements.define('lx-drop-story', class extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      const body = this.querySelector('.lx-drop-body');
      if (!body) return;
      this.dataset.ready = 'true';
      const element = (tag, className, text) => {
        const node = document.createElement(tag);
        node.className = className;
        if (text) node.textContent = text;
        return node;
      };
      const mediaType = node => {
        const media = node.matches('img,video,iframe') ? node : node.querySelector('img,video,iframe');
        // Keep captions, linked images, mixed prose and complex embeds in their authored order.
        if (!media || node.textContent.trim() || node.querySelector('a,figcaption') || node.querySelectorAll('img,video,iframe').length > 1) return null;
        return media.tagName === 'IMG' ? 'image' : 'film';
      };
      const nodes = [...body.children];
      let start = nodes.length;
      while (start > 0 && mediaType(nodes[start - 1])) start--;
      const tail = nodes.slice(start);
      const copy = nodes.slice(0, start);
      // Only curate an appended media collection. Interleaved editorial content stays intact.
      if (tail.length < 2 || copy.some(n => n.matches('img,video,iframe') || n.querySelector('img,video,iframe'))) return;
      const photos = tail.filter(n => mediaType(n) === 'image');
      const films = tail.filter(n => mediaType(n) === 'film');
      if (photos.length) {
        const gallery = element('section', 'lx-story-gallery');
        gallery.setAttribute('aria-label', 'Edition photographs');
        photos.forEach((node, i) => {
          const figure = element('figure', 'lx-story-photo');
          const frame = element('div', 'lx-story-photo-frame');
          frame.append(node);
          figure.append(frame, element('figcaption', 'lx-story-photo-caption', `THE EDIT / ${String(i + 1).padStart(2, '0')}`));
          gallery.append(figure);
        });
        body.prepend(gallery);
      }
      if (copy.length && copy[0].tagName === 'H2' && copy.every(n => ['H2','P','UL','OL'].includes(n.tagName))) {
        const chapters = element('div', 'lx-story-chapters');
        let chapter;
        let index = 0;
        copy.forEach(node => {
          if (node.tagName === 'H2') {
            chapter = element('section', 'lx-story-chapter');
            chapter.append(element('span', 'lx-story-chapter-number', String(++index).padStart(2, '0')));
            chapters.append(chapter);
          }
          chapter.append(node);
        });
        body.append(chapters);
      }
      if (films.length) {
        const cinema = element('section', 'lx-story-cinema');
        const heading = element('header', 'lx-story-cinema-heading');
        const title = element('h2', '', 'In motion.');
        heading.append(element('p', 'lx-drop-kicker', 'THE FILM NOTES'), title, element('span', '', `${films.length} ${films.length === 1 ? 'film' : 'films'} · Play to explore`));
        const grid = element('div', 'lx-story-films');
        films.forEach((node, i) => {
          const film = node.matches('video,iframe') ? node : node.querySelector('video,iframe');
          const card = element('figure', 'lx-story-film');
          const title = film.getAttribute('aria-label') || film.getAttribute('title') || `Film ${i + 1}`;
          const caption = element('figcaption', 'lx-story-film-caption');
          caption.append(element('span', '', String(i + 1).padStart(2, '0')), element('h3', '', title));
          card.append(node, caption);
          grid.append(card);
        });
        cinema.append(heading, grid);
        body.append(cinema);
      }
      body.classList.add('lx-drop-body--editorial');
      // Do not let two native films compete for audio; preserve all native controls.
      body.querySelectorAll('video').forEach(video => video.addEventListener('play', () => {
        body.querySelectorAll('video').forEach(other => { if (other !== video) other.pause(); });
      }));
    }
  });
}
